import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Observable, tap } from 'rxjs';
import {
  Feature,
  IsNumberValueProperty,
  SetNumberValueCommands,
  FeaturePropertyKey,
  FeatureCommands,
  WallboxSmartChargingModeProperties,
  WallboxSmartChargingModeCommands,
  DeviceIds,
} from '@vi-iot/client';
import { FeatureNames } from '@vi-iot/client/apis/operational-data/operational-data.constants';
import { DevicesStore, InstallationFeaturesStore, RichDevice } from '@store';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { PillComponent } from 'src/app/shared/components/pill/pill.component';
import { PillType } from 'src/app/shared/components/pill/pill.model';
import { BannerComponent } from '@components';
import { CommonUIModule } from '@shared-ui';
import { ViToastService } from '@vi-iot/ui/components/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';

export interface WallboxSettingsDialogData {
  gatewaySerialNumber: string;
}

@Component({
  selector: 'app-wallbox-settings-dialog',
  standalone: true,
  templateUrl: './wallbox-settings-dialog.component.html',
  styleUrls: ['./wallbox-settings-dialog.component.scss'],
  imports: [
    CommonStandaloneModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRadioModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    PillComponent,
    BannerComponent,
    CommonUIModule,
  ],
})
export class WallboxSettingsDialogComponent implements OnInit {
  charginglevels: number[] = [];
  chargingTimeWindow = 12;
  chargingTimeWindowOptions: number[] = [];
  isBatteryCapacityOutOfRange = false;
  batteryCapacityRange = { min: 1, max: 100 };
  isChargingLevelsValid = true;
  plugInChargeLevel = 0;
  targetChargeLevel = 0;
  readonly pillType = PillType;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly gatewaySerialNumber = '';
  private deviceId = '';

  batteryCapacity$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  plugInLevel$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  smartChargingMode$: Observable<
    Feature<WallboxSmartChargingModeProperties, WallboxSmartChargingModeCommands>
  >;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: WallboxSettingsDialogData,
    private dialogRef: MatDialogRef<WallboxSettingsDialogComponent>,
    private installationFeaturesStore: InstallationFeaturesStore,
    private deviceStore: DevicesStore,
    private toastService: ViToastService,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.deviceStore.getSelectedDevice$.subscribe((device: RichDevice): void => {
      this.initInstallationFeaturesWithDeviceId(device.id);
    });
  }

  initInstallationFeaturesWithDeviceId(deviceId: string): void {
    this.deviceId = deviceId;
    this.batteryCapacity$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.wallboxCapacityNominal, deviceId)
      .pipe(
        tap((feature: Feature<IsNumberValueProperty, SetNumberValueCommands>) => {
          const constraints = feature?.commands?.setValue?.params?.value?.constraints;
          this.batteryCapacityRange = {
            min: Number(constraints.min),
            max: Number(constraints.max),
          };
        })
      );

    this.plugInLevel$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.wallboxStateOfCharge, deviceId)
      .pipe(
        tap((feature: Feature<IsNumberValueProperty, SetNumberValueCommands>) => {
          this.plugInChargeLevel = Number(feature?.properties?.value?.value);
          const constraints = feature?.commands?.setValue?.params?.value?.constraints;
          this.charginglevels = [];
          for (let i = constraints.min; i <= constraints.max; i += constraints.stepping) {
            this.charginglevels.push(i);
          }
        })
      );

    this.smartChargingMode$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.wallboxSmartChargingMode, DeviceIds.Hems)
      .pipe(
        tap(
          (
            feature: Feature<WallboxSmartChargingModeProperties, WallboxSmartChargingModeCommands>
          ) => {
            const constraints = feature?.commands?.setTimer?.params?.value?.constraints;
            this.chargingTimeWindowOptions = [];
            for (let i = constraints.min; i <= constraints.max; i += constraints.stepping) {
              this.chargingTimeWindowOptions.push(i);
            }
            this.chargingTimeWindow = Number(feature?.properties?.timer?.value);
            this.targetChargeLevel = Number(feature?.properties?.stateOfCharge?.value);
          }
        )
      );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  updateBatteryCapacity(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);

    if (
      value < Number(this.batteryCapacityRange.min) ||
      value > Number(this.batteryCapacityRange.max)
    ) {
      this.isBatteryCapacityOutOfRange = true;

      return;
    }

    this.isBatteryCapacityOutOfRange = false;
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxCapacityNominal,
      value,
      FeaturePropertyKey.value,
      FeaturePropertyKey.value,
      FeatureCommands.setValue,
      this.data.gatewaySerialNumber,
      this.deviceId
    );
  }

  onPlugInLevelChange(chargeLevel: number): void {
    this.plugInChargeLevel = chargeLevel;

    if (this.plugInChargeLevel >= this.targetChargeLevel) {
      this.toastService.error({
        message: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.SETTINGS.CHARGING_LEVELS.ERROR.INVALID'
        ),
      });

      return;
    }

    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxStateOfCharge,
      chargeLevel,
      FeaturePropertyKey.value,
      FeaturePropertyKey.value,
      FeatureCommands.setValue,
      this.data.gatewaySerialNumber,
      this.deviceId
    );
  }

  onTargetChargeLevelChange(chargeLevel: number): void {
    this.targetChargeLevel = chargeLevel;

    if (this.targetChargeLevel <= this.plugInChargeLevel) {
      this.toastService.error({
        message: this.translateService.instant(
          '+INSTALLATIONS.INSTALLATION.WALLBOX.SMART_CHARGING.SETTINGS.CHARGING_LEVELS.ERROR.INVALID'
        ),
      });

      return;
    }

    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxSmartChargingMode,
      chargeLevel,
      FeaturePropertyKey.stateOfCharge,
      FeaturePropertyKey.value,
      FeatureCommands.setStateOfCharge,
      this.data.gatewaySerialNumber,
      DeviceIds.Hems
    );
  }

  onChargingTimeWindowChange(chargingTimeWindow: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.wallboxSmartChargingMode,
      chargingTimeWindow,
      FeaturePropertyKey.timer,
      FeaturePropertyKey.value,
      FeatureCommands.setTimer,
      this.data.gatewaySerialNumber,
      DeviceIds.Hems
    );
  }
}
